import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash, PencilSimple, UserCircle, ShieldCheck, Plus, CheckCircle, Prohibit, MagnifyingGlass } from "@phosphor-icons/react";
import { COUNTRIES, STATES_BY_COUNTRY } from "@/lib/locations";
import CountrySelect from "@/components/CountrySelect";
import PasswordInput from "@/components/PasswordInput";
import MultiClubSelect from "@/components/MultiClubSelect";
import { emitStatsChanged } from "@/lib/statsBus";

const roleColors = {
  admin: "bg-[#CCFF00]/20 text-[#CCFF00] border-[#CCFF00]/30",
  directivo: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  profesor: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  jugador: "bg-gray-500/20 text-gray-300 border-gray-500/30",
};

const isUserActive = (u) => u?.is_active !== false;

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", role: "", club_id: "", club_ids: [], category: "", experience_years: "", country: "", state: "", new_password: "", can_create_retas: false, can_create_tournaments: false, can_create_leagues: false });
  const [addDialog, setAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "", password: "", role: "jugador" });
  const [mergeDialog, setMergeDialog] = useState(false);
  const [mergeForm, setMergeForm] = useState({ user_id: "", guest_name: "" });
  const [mergeBusy, setMergeBusy] = useState(false);
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const loadUsers = () => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => toast.error("Error cargando usuarios")).finally(() => setLoading(false));
  };
  useEffect(() => {
    loadUsers();
    api.get("/clubs").then(({ data }) => setClubs(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (filterRole !== "all" && u.role !== filterRole) return false;
      if (filterStatus === "active" && !isUserActive(u)) return false;
      if (filterStatus === "inactive" && isUserActive(u)) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const hay = `${u.name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, filterRole, filterStatus, searchTerm]);

  const counts = useMemo(() => ({
    total: users.length,
    active: users.filter(isUserActive).length,
    inactive: users.filter((u) => !isUserActive(u)).length,
    pending_directivos: users.filter((u) => u.role === "directivo" && !u.club_id && !(u.club_ids && u.club_ids.length)).length,
  }), [users]);

  const openEdit = (u) => {
    const perms = u.permissions || {};
    setEditForm({
      name: u.name,
      email: u.email || "",
      phone: u.phone || "",
      role: u.role,
      club_id: u.club_id || "",
      club_ids: Array.isArray(u.club_ids) && u.club_ids.length
        ? u.club_ids
        : (u.club_id ? [u.club_id] : []),
      category: u.category || "",
      experience_years: u.experience_years ?? "",
      country: u.country || "",
      state: u.state || "",
      new_password: "",
      can_create_retas: !!perms.can_create_retas,
      can_create_tournaments: !!perms.can_create_tournaments,
      can_create_leagues: !!perms.can_create_leagues,
    });
    setEditDialog(u);
  };

  const handleEdit = async () => {
    if (!editDialog) return;
    try {
      const isMulti = editForm.role === "directivo" || editForm.role === "profesor";
      const clubIds = isMulti ? (editForm.club_ids || []).filter(Boolean) : [];
      const payload = {
        name: editForm.name,
        email: editForm.email || null,
        phone: editForm.phone || null,
        role: editForm.role,
        club_id: isMulti ? (clubIds[0] || null) : (editForm.club_id || null),
        club_ids: isMulti ? clubIds : [],
        category: editForm.category || null,
        experience_years: editForm.experience_years === "" ? null : parseFloat(editForm.experience_years),
        country: editForm.country || null,
        state: editForm.state || null,
      };
      if (editForm.new_password) payload.new_password = editForm.new_password;
      await api.put(`/admin/users/${editDialog.id}`, payload);
      // If the user is jugador/profesor, also sync per-resource creation permissions
      if (editForm.role === "jugador" || editForm.role === "profesor") {
        await api.put(`/admin/users/${editDialog.id}/permissions`, {
          can_create_retas: !!editForm.can_create_retas,
          can_create_tournaments: !!editForm.can_create_tournaments,
          can_create_leagues: !!editForm.can_create_leagues,
        });
      }
      toast.success("Usuario actualizado");
      emitStatsChanged("admin:user-updated", { userId: editDialog.id });
      setEditDialog(null);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users", { name: addForm.name, email: addForm.email || null, phone: addForm.phone || null, password: addForm.password, role: addForm.role });
      if (data.welcome_email_sent) {
        toast.success(`Usuario creado. Email de bienvenida enviado a ${data.email}`);
      } else if (data.email) {
        toast.warning("Usuario creado. Email NO se envio (configura SendGrid). Comparte la contrasena manualmente.", { duration: 8000 });
      } else {
        toast.success("Usuario creado (sin email)");
      }
      setAddDialog(false);
      setAddForm({ name: "", email: "", phone: "", password: "", role: "jugador" });
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleToggleStatus = async (u) => {
    const currentlyActive = isUserActive(u);
    const action = currentlyActive ? "desactivar" : "activar";
    if (!window.confirm(`¿Seguro que deseas ${action} el acceso de ${u.name}?`)) return;
    try {
      await api.put(`/admin/users/${u.id}/status`, { is_active: !currentlyActive });
      toast.success(currentlyActive ? "Acceso desactivado" : "Acceso activado");
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Eliminar a ${u.name}? No se puede deshacer.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Eliminado"); loadUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (currentUser?.role !== "admin") return <div className="card-tactical p-12 text-center"><p className="text-gray-400">Solo admin</p></div>;
  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Administracion</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Usuarios</h1>
          <p className="text-sm text-gray-400 mt-1">Control total de accesos a la plataforma</p>
        </div>
        <Button onClick={() => setMergeDialog(true)} variant="outline" className="border-[#CCFF00]/40 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase tracking-wide text-xs mr-2" data-testid="merge-guest-btn">
          Merge invitado
        </Button>
        <Button onClick={() => setAddDialog(true)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="add-user-btn">
          <Plus size={16} className="mr-2" /> Agregar Usuario
        </Button>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="user-stats">
        <div className="card-tactical p-4">
          <p className="text-xs uppercase tracking-wider text-gray-500">Total</p>
          <p className="text-3xl font-black text-white mt-1" data-testid="stat-total">{counts.total}</p>
        </div>
        <div className="card-tactical p-4">
          <p className="text-xs uppercase tracking-wider text-gray-500">Activos</p>
          <p className="text-3xl font-black text-[#CCFF00] mt-1" data-testid="stat-active">{counts.active}</p>
        </div>
        <div className="card-tactical p-4">
          <p className="text-xs uppercase tracking-wider text-gray-500">Bloqueados</p>
          <p className="text-3xl font-black text-red-400 mt-1" data-testid="stat-inactive">{counts.inactive}</p>
        </div>
        <div className="card-tactical p-4">
          <p className="text-xs uppercase tracking-wider text-gray-500">Directivos sin club</p>
          <p className="text-3xl font-black text-yellow-400 mt-1" data-testid="stat-pending-directivos">{counts.pending_directivos}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card-tactical p-4 flex flex-wrap gap-3 items-center" data-testid="user-filters">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            data-testid="search-users"
            placeholder="Buscar por nombre, email o telefono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-[#0A0A0A] border-white/20 text-white pl-10 h-10 focus:border-[#CCFF00]"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 w-[170px]" data-testid="filter-role"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10">
            <SelectItem value="all">Todos los roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="directivo">Directivo</SelectItem>
            <SelectItem value="profesor">Profesor</SelectItem>
            <SelectItem value="jugador">Jugador</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 w-[170px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10">
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="active">Solo activos</SelectItem>
            <SelectItem value="inactive">Solo bloqueados</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 ml-auto">Mostrando {filtered.length} / {users.length}</p>
      </div>

      <div className="card-tactical overflow-hidden" data-testid="users-table">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5">
              <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Usuario</TableHead>
              <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Contacto</TableHead>
              <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Rol</TableHead>
              <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Club</TableHead>
              <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Estado</TableHead>
              <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Registro</TableHead>
              <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-8">No hay usuarios que coincidan</TableCell></TableRow>
            )}
            {filtered.map((u) => {
              const active = isUserActive(u);
              const userClubIds = Array.isArray(u.club_ids) && u.club_ids.length
                ? u.club_ids
                : (u.club_id ? [u.club_id] : []);
              const clubNames = userClubIds
                .map((cid) => clubs.find((c) => c.id === cid)?.name)
                .filter(Boolean);
              const clubName = clubNames.length === 0
                ? "—"
                : clubNames.length === 1
                  ? clubNames[0]
                  : `${clubNames[0]} +${clubNames.length - 1}`;
              return (
                <TableRow key={u.id} className="border-white/5 hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                  <TableCell><div className="flex items-center gap-3"><UserCircle size={28} weight="duotone" className={active ? "text-gray-500" : "text-gray-700"} /><span className={`font-semibold ${active ? "text-white" : "text-gray-500 line-through"}`}>{u.name}</span></div></TableCell>
                  <TableCell><div className="text-sm">{u.email && <p className="text-gray-400">{u.email}</p>}{u.phone && <p className="text-gray-500 text-xs">{u.phone}</p>}</div></TableCell>
                  <TableCell><Badge variant="outline" className={`badge-status ${roleColors[u.role]}`}>{u.role === "admin" && <ShieldCheck size={12} className="mr-1 inline" />}{u.role}</Badge></TableCell>
                  <TableCell className="text-sm text-gray-400" title={clubNames.join(", ")}>{clubName}</TableCell>
                  <TableCell>
                    {active ? (
                      <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30" data-testid={`status-active-${u.id}`}>Activo</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30" data-testid={`status-inactive-${u.id}`}>Bloqueado</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-500 text-xs">{u.created_at?.slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    {u.id !== currentUser?.id ? (
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleStatus(u)}
                          className={`h-8 w-8 ${active ? "text-gray-400 hover:text-red-400" : "text-gray-400 hover:text-green-400"}`}
                          title={active ? "Desactivar acceso" : "Activar acceso"}
                          data-testid={`toggle-status-${u.id}`}
                        >
                          {active ? <Prohibit size={16} /> : <CheckCircle size={16} />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} className="text-gray-400 hover:text-[#CCFF00] h-8 w-8" title="Editar" data-testid={`edit-user-${u.id}`}><PencilSimple size={16} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(u)} className="text-gray-400 hover:text-red-400 h-8 w-8" title="Eliminar" data-testid={`delete-user-${u.id}`}><Trash size={16} /></Button>
                      </div>
                    ) : <span className="text-xs text-gray-600">Tu cuenta</span>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Editar Usuario</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nombre</Label>
              <Input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm(p => ({...p, name: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
            <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Email</Label>
              <Input data-testid="edit-email" value={editForm.email} onChange={(e) => setEditForm(p => ({...p, email: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
            <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Telefono</Label>
              <Input data-testid="edit-phone" value={editForm.phone} onChange={(e) => setEditForm(p => ({...p, phone: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Pais</Label>
                <CountrySelect
                  value={editForm.country}
                  onChange={(v) => setEditForm(p => ({...p, country: v, state: v !== p.country ? "" : p.state}))}
                  testid="edit-country"
                  placeholder="Sin pais"
                />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Estado</Label>
                <Select value={editForm.state || "__none"} onValueChange={(v) => setEditForm(p => ({...p, state: v === "__none" ? "" : v}))} disabled={!editForm.country}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11 disabled:opacity-50" data-testid="edit-state"><SelectValue placeholder="Sin estado" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[260px]">
                    <SelectItem value="__none">Sin estado</SelectItem>
                    {(STATES_BY_COUNTRY[editForm.country] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Rol</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm(p => ({...p, role: v}))}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="jugador">Jugador</SelectItem><SelectItem value="profesor">Profesor</SelectItem>
                  <SelectItem value="directivo">Directivo</SelectItem><SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select></div>
            {(editForm.role === "directivo" || editForm.role === "profesor") ? (
              <div data-testid="edit-clubs-multi-block">
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">
                  Clubes asignados {editForm.role === "directivo" && <span className="text-[#CCFF00]">(al menos uno)</span>}
                </Label>
                <MultiClubSelect
                  clubs={clubs}
                  value={editForm.club_ids}
                  onChange={(ids) => setEditForm(p => ({ ...p, club_ids: ids, club_id: ids[0] || "" }))}
                  testid="edit-club-multi"
                  emptyLabel={editForm.role === "directivo" ? "Debe tener al menos un club" : "Sin clubes asignados"}
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  {editForm.role === "profesor"
                    ? "El profesor podrá auto-asignarse clubes desde su propio perfil."
                    : "El directivo administrará los clubes seleccionados."}
                </p>
              </div>
            ) : (
              <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Club asignado</Label>
                <Select value={editForm.club_id || "__none"} onValueChange={(v) => setEditForm(p => ({...p, club_id: v === "__none" ? "" : v}))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="edit-club-select"><SelectValue placeholder="Sin club" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="__none">Sin club</SelectItem>
                    {clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(editForm.role === "jugador" || editForm.role === "profesor") && (
              <>
                {editForm.role === "jugador" && (
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Categoria <span className="text-[#CCFF00]">(admin puede cambiarla)</span></Label>
                    <Select value={editForm.category || "__none"} onValueChange={(v) => setEditForm(p => ({...p, category: v === "__none" ? "" : v}))}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="edit-category"><SelectValue placeholder="Sin categoria" /></SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="__none">Sin categoria</SelectItem>
                        <SelectItem value="open">Open (mayor nivel)</SelectItem>
                        <SelectItem value="1">Primera</SelectItem>
                        <SelectItem value="2">Segunda</SelectItem>
                        <SelectItem value="3">Tercera</SelectItem>
                        <SelectItem value="4">Cuarta</SelectItem>
                        <SelectItem value="5">Quinta</SelectItem>
                        <SelectItem value="6">Sexta</SelectItem>
                      </SelectContent>
                    </Select>
                    {editDialog?.category_overrides?.length > 0 && (
                      <p className="text-[11px] text-green-400 mt-1.5" data-testid="unlock-categories-info">
                        ✓ Desbloqueado para jugar en: {editDialog.category_overrides.join(", ")}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        const cat = window.prompt("Categoria a desbloquear (6/5/4/3/2/1/open)", editForm.category || "3");
                        if (!cat) return;
                        try {
                          await api.put(`/admin/users/${editDialog.id}/unlock-category`, { category: cat });
                          toast.success(`Categoria ${cat} desbloqueada`);
                          loadUsers();
                          setEditDialog(null);
                        } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
                      }}
                      className="mt-2 text-[11px] text-[#CCFF00] hover:underline"
                      data-testid="unlock-category-btn"
                    >
                      Desbloquear categoria (permitir seguir jugando aunque haya ganado 2+ torneos)
                    </button>
                  </div>
                )}
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">
                    {editForm.role === "profesor" ? "Tiempo dando clinicas (anos)" : "Tiempo jugando (anos)"}
                  </Label>
                  <Input
                    data-testid="edit-experience"
                    type="number"
                    min="0"
                    max="80"
                    step="0.5"
                    value={editForm.experience_years}
                    onChange={(e) => setEditForm(p => ({...p, experience_years: e.target.value}))}
                    placeholder="Ej: 2.5"
                    className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600"
                  />
                </div>
              </>
            )}
            {(editForm.role === "jugador" || editForm.role === "profesor") && (
              <div className="rounded border border-[#CCFF00]/20 bg-[#CCFF00]/5 p-3 space-y-2" data-testid="user-permissions-block">
                <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#CCFF00]">Permisos para organizar eventos</p>
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  Habilita a este {editForm.role} para crear Retas, Torneos o Ligas. Sólo el creador (o un admin) podrá editar o eliminar los eventos que cree.
                </p>
                <label className="flex items-center justify-between gap-3 text-xs text-gray-200 cursor-pointer">
                  <span>Puede crear Retas</span>
                  <input
                    type="checkbox"
                    data-testid="perm-create-retas"
                    checked={!!editForm.can_create_retas}
                    onChange={(e) => setEditForm(p => ({ ...p, can_create_retas: e.target.checked }))}
                    className="h-4 w-4 accent-[#CCFF00]"
                  />
                </label>
                <label className="flex items-center justify-between gap-3 text-xs text-gray-200 cursor-pointer">
                  <span>Puede crear Torneos</span>
                  <input
                    type="checkbox"
                    data-testid="perm-create-tournaments"
                    checked={!!editForm.can_create_tournaments}
                    onChange={(e) => setEditForm(p => ({ ...p, can_create_tournaments: e.target.checked }))}
                    className="h-4 w-4 accent-[#CCFF00]"
                  />
                </label>
                <label className="flex items-center justify-between gap-3 text-xs text-gray-200 cursor-pointer">
                  <span>Puede crear Ligas</span>
                  <input
                    type="checkbox"
                    data-testid="perm-create-leagues"
                    checked={!!editForm.can_create_leagues}
                    onChange={(e) => setEditForm(p => ({ ...p, can_create_leagues: e.target.checked }))}
                    className="h-4 w-4 accent-[#CCFF00]"
                  />
                </label>
              </div>
            )}
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nueva contrasena <span className="text-gray-600">(opcional)</span></Label>
              <PasswordInput
                data-testid="edit-new-password"
                value={editForm.new_password}
                onChange={(e) => setEditForm(p => ({...p, new_password: e.target.value}))}
                placeholder="Dejar vacio para no cambiar"
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600"
              />
            </div>
            <DialogFooter><Button onClick={handleEdit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide w-full" data-testid="save-edit-btn">Guardar Cambios</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Agregar Usuario</DialogTitle></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 p-3 rounded-none">
              <p className="text-[11px] text-[#CCFF00] leading-relaxed">
                Se enviara un email automatico al usuario con sus credenciales e instrucciones para iniciar sesion.
              </p>
            </div>
            <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nombre</Label>
              <Input data-testid="add-name" value={addForm.name} onChange={(e) => setAddForm(p => ({...p, name: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" required /></div>
            <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Email</Label>
              <Input data-testid="add-email" type="email" value={addForm.email} onChange={(e) => setAddForm(p => ({...p, email: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
            <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Telefono</Label>
              <Input data-testid="add-phone" value={addForm.phone} onChange={(e) => setAddForm(p => ({...p, phone: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" /></div>
            <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Contrasena</Label>
              <PasswordInput data-testid="add-password" value={addForm.password} onChange={(e) => setAddForm(p => ({...p, password: e.target.value}))} className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" required /></div>
            <div><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Rol</Label>
              <Select value={addForm.role} onValueChange={(v) => setAddForm(p => ({...p, role: v}))}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="add-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="jugador">Jugador</SelectItem><SelectItem value="profesor">Profesor</SelectItem>
                  <SelectItem value="directivo">Directivo</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select></div>
            <DialogFooter><Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide w-full" data-testid="add-user-submit">Crear Usuario</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Merge Guest Dialog */}
      <Dialog open={mergeDialog} onOpenChange={setMergeDialog}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="merge-guest-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Mergear invitado a usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-400">Busca el usuario real (ya registrado) y escribe el nombre exacto tal como aparece en los partidos como invitado. Al confirmar, todas las apariciones se reasignarán a ese usuario y las estadísticas se recalcularán.</p>
            <div>
              <Label className="text-xs uppercase">Usuario registrado</Label>
              <Select value={mergeForm.user_id} onValueChange={(v) => setMergeForm((p) => ({ ...p, user_id: v }))}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="merge-user-select"><SelectValue placeholder="Buscar usuario..." /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-80">
                  {users.filter(u => u.role !== "admin").map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.email || u.phone || u.id.slice(0, 6)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase">Nombre del invitado (exacto)</Label>
              <Input value={mergeForm.guest_name} onChange={(e) => setMergeForm((p) => ({ ...p, guest_name: e.target.value }))} placeholder="ej. Jorge Huerta" className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="merge-guest-name" />
              <p className="text-[10px] text-gray-500 mt-1">La búsqueda es case-insensitive.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMergeDialog(false)} className="border-white/10">Cancelar</Button>
              <Button
                disabled={mergeBusy || !mergeForm.user_id || !mergeForm.guest_name.trim()}
                onClick={async () => {
                  setMergeBusy(true);
                  try {
                    const { data } = await api.post("/admin/merge-guest", { user_id: mergeForm.user_id, guest_name: mergeForm.guest_name.trim() });
                    const total = Object.values(data.modified || {}).reduce((a, b) => a + b, 0);
                    toast.success(`${total} registros migrados a ${data.target_user.name}`);
                    setMergeDialog(false);
                    setMergeForm({ user_id: "", guest_name: "" });
                    loadUsers();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Error al mergear");
                  } finally {
                    setMergeBusy(false);
                  }
                }}
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase"
                data-testid="merge-guest-submit"
              >
                {mergeBusy ? "Mergeando..." : "Mergear"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
